/**
 * Offline-first engine. Mirrors whitelisted queries to IndexedDB (so the diary opens with data with
 * no network) and drains a durable mutation outbox in order when the connection returns. A Wi-Fi
 * blip must never block checkout or rebooking (brief WS2) — till/rebook mutations enqueue here and
 * reconcile in the background. Safe to replay: appointment creates carry an idempotencyKey.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiClientError, unwrap } from '../api.js';
import { PERSISTED_PREFIXES, queryClient } from '../query.js';
import { useToast } from '../toast.js';
import {
  cacheGetAll,
  cachePut,
  outboxAll,
  outboxCount,
  outboxDelete,
  outboxPut,
  type OutboxItem,
  type OutboxKind,
} from './db.js';

interface OfflineApi {
  online: boolean;
  pending: number;
  /** Queue a mutation that survives reload/offline. Returns the outbox id. */
  enqueue: (
    kind: OutboxKind,
    payload: unknown,
    invalidate: ReadonlyArray<ReadonlyArray<unknown>>,
  ) => Promise<string>;
  flushNow: () => Promise<void>;
}

const OfflineContext = createContext<OfflineApi | null>(null);

async function process(item: OutboxItem): Promise<void> {
  switch (item.kind) {
    case 'appointment.create': {
      // 409s are impossible here: a replay with the same idempotencyKey returns the original.
      unwrap(await api.POST('/v1/appointments', { body: item.payload as never }));
      return;
    }
    case 'appointment.update': {
      const p = item.payload as { id: string; body: unknown };
      unwrap(
        await api.PATCH('/v1/appointments/{id}', {
          params: { path: { id: p.id } },
          body: p.body as never,
        }),
      );
      return;
    }
    case 'queue.action': {
      const p = item.payload as { id: string; action: string };
      unwrap(
        await api.POST('/v1/queue/{id}/action', {
          params: { path: { id: p.id } },
          body: { action: p.action } as never,
        }),
      );
      return;
    }
    case 'queue.join': {
      unwrap(await api.POST('/v1/queue/join', { body: item.payload as never }));
      return;
    }
  }
}

export function OfflineProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [pending, setPending] = useState(0);
  const flushing = useRef(false);

  const refreshPending = useCallback(async () => setPending(await outboxCount()), []);

  const flushNow = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      const items = await outboxAll();
      for (const item of items) {
        try {
          await process(item);
          await outboxDelete(item.id);
          for (const key of item.invalidate) await queryClient.invalidateQueries({ queryKey: key });
        } catch (err) {
          const fatal =
            err instanceof ApiClientError &&
            err.status >= 400 &&
            err.status < 500 &&
            !err.isConflict;
          if (fatal) {
            // Dead-letter: a 4xx won't get better on retry. Drop it and tell the user.
            await outboxDelete(item.id);
            toast.error(`A queued change was rejected: ${err.message}`);
          } else {
            // Transient/offline: keep the item and preserve order; retry on next online tick.
            await outboxPut({ ...item, tries: item.tries + 1, lastError: String(err) });
            break;
          }
        }
      }
    } finally {
      flushing.current = false;
      await refreshPending();
    }
  }, [toast, refreshPending]);

  const enqueue = useCallback<OfflineApi['enqueue']>(
    async (kind, payload, invalidate) => {
      const id = crypto.randomUUID();
      await outboxPut({ id, kind, payload, invalidate, createdAt: Date.now(), tries: 0 });
      await refreshPending();
      if (navigator.onLine) void flushNow();
      return id;
    },
    [flushNow, refreshPending],
  );

  // Hydrate the read replica + load pending count once.
  useEffect(() => {
    void (async () => {
      const entries = await cacheGetAll();
      for (const e of entries) {
        try {
          const key = JSON.parse(e.key) as unknown[];
          if (queryClient.getQueryData(key) === undefined) queryClient.setQueryData(key, e.data);
        } catch {
          /* corrupt entry — ignore */
        }
      }
      await refreshPending();
      if (navigator.onLine) void flushNow();
    })();
  }, [flushNow, refreshPending]);

  // Persist whitelisted queries to IndexedDB (debounced) as they update.
  useEffect(() => {
    const dirty = new Map<string, { key: unknown[]; data: unknown }>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const persist = async () => {
      const items = [...dirty.values()];
      dirty.clear();
      for (const it of items) await cachePut(JSON.stringify(it.key), it.data);
    };
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      const q = event.query;
      const key = q.queryKey as unknown[];
      const head = Array.isArray(key) ? String(key[0]) : '';
      if (!PERSISTED_PREFIXES.includes(head)) return;
      if (q.state.status !== 'success' || q.state.data === undefined) return;
      dirty.set(JSON.stringify(key), { key, data: q.state.data });
      clearTimeout(timer);
      timer = setTimeout(() => void persist(), 400);
    });
    return () => {
      unsub();
      clearTimeout(timer);
    };
  }, []);

  // Network transitions + tab refocus drive the flush.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void flushNow();
    };
    const goOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) void flushNow();
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [flushNow]);

  return (
    <OfflineContext.Provider value={{ online, pending, enqueue, flushNow }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline(): OfflineApi {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
}
